(function () {
  if (window.SolenaSizeRecommender) return;

  const qs = (selector, root = document) => root.querySelector(selector);
  const qsa = (selector, root = document) => Array.from(root.querySelectorAll(selector));
  const STORAGE_KEY = 'solena-size-recommender';

  const labels = {
    fixtureType: {
      pendant: 'Pendant Light',
      chandelier: 'Chandelier',
      'linear-pendant': 'Linear Pendant',
      'flush-mount': 'Flush Mount',
      'semi-flush': 'Semi-Flush Mount',
      'wall-light': 'Wall Light'
    },
    placement: {
      'dining-table': 'Dining table',
      'kitchen-island': 'Kitchen island',
      'coffee-table': 'Coffee table',
      bedside: 'Bedside area',
      'open-floor': 'Open floor area',
      'entry-console': 'Entry console',
      'no-furniture': 'No furniture below',
      other: 'Other'
    }
  };

  const defaultState = () => ({
    step: 1,
    unit: 'in',
    fixtureType: '',
    roomType: '',
    placement: '',
    tableShape: 'rectangle',
    tableLength: '',
    tableWidth: '',
    tableDiameter: '',
    islandLength: '',
    islandWidth: '',
    fixtureCountPreference: 'not-sure',
    roomLength: '',
    roomWidth: '',
    ceilingHeight: '',
    slopedCeiling: 'no',
    slopeNotes: '',
    result: null
  });

  const toInches = (value, unit) => {
    const number = Number(value);
    if (!Number.isFinite(number) || number <= 0) return 0;
    if (unit === 'ft') return number * 12;
    if (unit === 'cm') return number / 2.54;
    if (unit === 'm') return number * 39.3701;
    return number;
  };

  const fromInches = (value, unit) => {
    if (unit === 'ft') return value / 12;
    if (unit === 'cm') return value * 2.54;
    if (unit === 'm') return value / 39.3701;
    return value;
  };

  const unitLabel = (unit) => ({ in: 'in', ft: 'ft', cm: 'cm', m: 'm' }[unit] || 'in');

  const formatRange = (min, max, unit) => {
    const a = fromInches(min, unit);
    const b = fromInches(max, unit);
    const decimals = unit === 'in' || unit === 'cm' ? 0 : 1;
    return `${a.toFixed(decimals)}-${b.toFixed(decimals)} ${unitLabel(unit)}`;
  };

  const formatSingle = (value, unit) => {
    const converted = fromInches(value, unit);
    const decimals = unit === 'in' || unit === 'cm' ? 0 : 1;
    return `${converted.toFixed(decimals)} ${unitLabel(unit)}`;
  };

  const parseDimensionFromTitle = (title) => {
    const text = String(title || '');
    const read = (patterns) => {
      for (const pattern of patterns) {
        const match = text.match(pattern);
        if (match) return Number(match[1]);
      }
      return null;
    };
    return {
      diameter: read([/(?:dia|diameter|ø)\.?\s*([\d.]+)/i, /D\s*([\d.]+)/i]),
      width: read([/W\s*([\d.]+)/i, /width\s*([\d.]+)/i]),
      length: read([/L\s*([\d.]+)/i, /length\s*([\d.]+)/i]),
      height: read([/H\s*([\d.]+)/i, /height\s*([\d.]+)/i])
    };
  };

  const normalizeVariant = (variant) => {
    const parsed = parseDimensionFromTitle(variant.title);
    const dimensions = variant.dimensions || {};
    return {
      ...variant,
      dimensions: {
        diameter: Number(dimensions.diameter) || parsed.diameter || null,
        width: Number(dimensions.width) || parsed.width || null,
        length: Number(dimensions.length) || parsed.length || null,
        height: Number(dimensions.height) || parsed.height || null,
        dropMin: Number(dimensions.dropMin) || null,
        dropMax: Number(dimensions.dropMax) || null
      }
    };
  };

  class SizeRecommender {
    constructor(root) {
      this.root = root;
      this.state = defaultState();
      this.product = null;
      this.bestVariant = null;
      this.localSave = root.dataset.enableLocalSave === 'true';
      this.storageKey = `${STORAGE_KEY}:${root.dataset.productHandle || 'guide'}`;
      this.state.unit = root.dataset.defaultUnit || 'in';
      this.loadProduct();
      this.loadSavedState();
      this.bind();
      this.syncInputs();
      this.render();
    }

    loadProduct() {
      const productScript = qs('[data-solena-size-product]', this.root);
      if (!productScript) return;
      try {
        this.product = JSON.parse(productScript.textContent);
        this.product.variants = (this.product.variants || []).map(normalizeVariant);
      } catch (error) {
        this.product = null;
      }
    }

    loadSavedState() {
      if (!this.localSave) return;
      try {
        const saved = JSON.parse(window.localStorage.getItem(this.storageKey) || '{}');
        this.state = { ...this.state, ...saved, step: 1 };
      } catch (error) {
        return;
      }
    }

    saveState() {
      if (!this.localSave) return;
      try {
        const saved = { ...this.state, step: 1, result: null };
        window.localStorage.setItem(this.storageKey, JSON.stringify(saved));
      } catch (error) {
        return;
      }
    }

    bind() {
      qsa('[data-solena-size-option]', this.root).forEach((button) => {
        button.addEventListener('click', () => {
          this.state[button.dataset.solenaSizeOption] = button.dataset.value;
          this.saveState();
          this.render();
        });
      });

      qsa('[data-solena-size-input]', this.root).forEach((input) => {
        input.addEventListener('input', () => this.updateFromInputs());
        input.addEventListener('change', () => this.updateFromInputs());
      });

      qsa('[data-solena-size-unit]', this.root).forEach((button) => {
        button.addEventListener('click', () => {
          const nextUnit = button.dataset.solenaSizeUnit;
          this.convertStoredMeasurements(this.state.unit, nextUnit);
          this.state.unit = nextUnit;
          this.saveState();
          this.syncInputs();
          this.render();
        });
      });

      qsa('[data-solena-ceiling-ft]', this.root).forEach((button) => {
        button.addEventListener('click', () => {
          const inches = Number(button.dataset.solenaCeilingFt) * 12;
          this.state.ceilingHeight = String(Math.round(fromInches(inches, this.state.unit) * 10) / 10);
          this.saveState();
          this.syncInputs();
          this.render();
        });
      });

      qs('[data-solena-size-prev]', this.root)?.addEventListener('click', () => {
        this.state.step = Math.max(1, this.state.step - 1);
        this.render(true);
      });

      qs('[data-solena-size-next]', this.root)?.addEventListener('click', () => {
        if (!this.validateStep()) return;
        if (this.state.step === 4) {
          this.state.result = this.calculate();
          this.state.step = 5;
          this.pushEvent();
        } else if (this.state.step < 5) {
          this.state.step += 1;
        }
        this.render(true);
      });

      qs('[data-size-action="restart"]', this.root)?.addEventListener('click', () => {
        this.state = defaultState();
        this.state.unit = this.root.dataset.defaultUnit || 'in';
        this.bestVariant = null;
        try { window.localStorage.removeItem(this.storageKey); } catch (error) {}
        this.syncInputs();
        this.render(true);
      });

      qs('[data-size-action="save"]', this.root)?.addEventListener('click', () => this.saveRecommendation());
      qs('[data-size-action="select-variant"]', this.root)?.addEventListener('click', () => this.selectRecommendedVariant());
    }

    updateFromInputs() {
      qsa('[data-solena-size-input]', this.root).forEach((input) => {
        this.state[input.dataset.solenaSizeInput] = input.value;
      });
      this.saveState();
      this.render();
    }

    syncInputs() {
      qsa('[data-solena-size-input]', this.root).forEach((input) => {
        const key = input.dataset.solenaSizeInput;
        if (this.state[key] !== undefined) input.value = this.state[key];
      });
    }

    convertStoredMeasurements(fromUnit, toUnit) {
      if (fromUnit === toUnit) return;
      [
        'tableLength',
        'tableWidth',
        'tableDiameter',
        'islandLength',
        'islandWidth',
        'roomLength',
        'roomWidth',
        'ceilingHeight'
      ].forEach((key) => {
        if (!this.state[key]) return;
        const inches = toInches(this.state[key], fromUnit);
        if (!inches) return;
        const converted = fromInches(inches, toUnit);
        this.state[key] = String(Math.round(converted * 10) / 10);
      });
    }

    activeMeasurementGroup() {
      if (this.state.placement === 'kitchen-island' || this.state.roomType === 'kitchen') return 'island';
      if (this.state.placement === 'dining-table' || this.state.fixtureType === 'linear-pendant') return 'table';
      return 'room';
    }

    render(shouldFocus = false) {
      qsa('[data-solena-size-step]', this.root).forEach((step) => {
        const active = Number(step.dataset.solenaSizeStep) === this.state.step;
        step.hidden = !active;
        step.classList.toggle('is-active', active);
      });

      const progress = qs('[data-solena-size-progress]', this.root);
      if (progress) progress.style.width = `${(this.state.step / 5) * 100}%`;
      const count = qs('[data-solena-size-count]', this.root);
      if (count) count.textContent = `Step ${this.state.step} of 5`;

      qsa('[data-solena-size-option]', this.root).forEach((button) => {
        button.classList.toggle('is-selected', this.state[button.dataset.solenaSizeOption] === button.dataset.value);
      });
      qsa('[data-solena-size-unit]', this.root).forEach((button) => {
        button.classList.toggle('is-selected', this.state.unit === button.dataset.solenaSizeUnit);
      });
      qsa('[data-solena-ceiling-ft]', this.root).forEach((button) => {
        const buttonValue = Number(button.dataset.solenaCeilingFt);
        const ceilingFeet = toInches(this.state.ceilingHeight, this.state.unit) / 12;
        button.classList.toggle('is-selected', Math.round(ceilingFeet) === buttonValue);
      });

      const group = this.activeMeasurementGroup();
      qsa('[data-solena-measure-group]', this.root).forEach((item) => {
        item.hidden = item.dataset.solenaMeasureGroup !== group;
      });

      const previous = qs('[data-solena-size-prev]', this.root);
      const next = qs('[data-solena-size-next]', this.root);
      if (previous) previous.disabled = this.state.step === 1;
      if (next) {
        next.hidden = this.state.step === 5;
        next.textContent = this.state.step === 4 ? 'See My Recommendation' : 'Continue';
      }

      this.renderSummary();
      if (this.state.step === 5) this.renderResult();
      if (shouldFocus) {
        const heading = qs('.solena-size-step.is-active h2', this.root);
        heading?.setAttribute('tabindex', '-1');
        heading?.focus({ preventScroll: this.root.dataset.mode === 'drawer' });
      }
    }

    renderSummary() {
      const set = (key, value) => {
        const el = qs(`[data-size-summary="${key}"]`, this.root);
        if (el) el.textContent = value || 'Not selected';
      };
      set('fixtureType', labels.fixtureType[this.state.fixtureType]);
      set('placement', labels.placement[this.state.placement]);
      const group = this.activeMeasurementGroup();
      if (group === 'island') {
        set('measurements', this.state.islandLength ? `Island ${this.state.islandLength} x ${this.state.islandWidth || '?'} ${unitLabel(this.state.unit)}` : '');
      } else if (group === 'table') {
        set('measurements', this.state.tableDiameter ? `Table dia. ${this.state.tableDiameter} ${unitLabel(this.state.unit)}` : (this.state.tableLength ? `Table ${this.state.tableLength} x ${this.state.tableWidth || '?'} ${unitLabel(this.state.unit)}` : ''));
      } else {
        set('measurements', this.state.roomLength ? `Room ${this.state.roomLength} x ${this.state.roomWidth || '?'} ${unitLabel(this.state.unit)}` : '');
      }
      set('ceilingHeight', this.state.ceilingHeight ? `${this.state.ceilingHeight} ${unitLabel(this.state.unit)}` : '');
    }

    showAlert(message) {
      const alert = qs('[data-solena-size-alert]', this.root);
      if (!alert) return;
      alert.textContent = message;
      alert.hidden = false;
    }

    clearAlert() {
      const alert = qs('[data-solena-size-alert]', this.root);
      if (alert) alert.hidden = true;
    }

    validateStep() {
      this.clearAlert();
      if (this.state.step === 1 && !this.state.fixtureType) {
        this.showAlert('Please choose a fixture type.');
        return false;
      }
      if (this.state.step === 2 && (!this.state.roomType || !this.state.placement)) {
        this.showAlert('Please choose the room and placement.');
        return false;
      }
      if (this.state.step === 3) {
        const group = this.activeMeasurementGroup();
        if (group === 'island' && !toInches(this.state.islandLength, this.state.unit)) {
          this.showAlert('Please enter your island length.');
          return false;
        }
        if (group === 'table' && !toInches(this.state.tableDiameter || this.state.tableWidth, this.state.unit)) {
          this.showAlert('Please enter your table width or diameter.');
          return false;
        }
        if (group === 'room' && (!toInches(this.state.roomLength, this.state.unit) || !toInches(this.state.roomWidth, this.state.unit))) {
          this.showAlert('Please enter your room length and width.');
          return false;
        }
      }
      if (this.state.step === 4 && !toInches(this.state.ceilingHeight, this.state.unit)) {
        this.showAlert('Please enter your ceiling height.');
        return false;
      }
      return true;
    }

    calculate() {
      const unit = this.state.unit;
      const group = this.activeMeasurementGroup();
      const fixture = this.state.fixtureType;
      const ceiling = toInches(this.state.ceilingHeight, unit) || 96;
      const ceilingFt = ceiling / 12;
      const extraDrop = Math.max(0, ceilingFt - 8) * 3;
      let min = 18;
      let max = 30;
      let lengthMin = null;
      let lengthMax = null;
      let quantity = '1 fixture';
      let height = 'Confirm final mounting height with your installer.';
      let why = '';
      let warning = '';
      let sizeLabel = '';

      if (group === 'table') {
        const diameter = toInches(this.state.tableDiameter, unit);
        const tableWidth = diameter || toInches(this.state.tableWidth, unit);
        const tableLength = toInches(this.state.tableLength, unit);
        min = tableWidth * 0.5;
        max = tableWidth * 0.67;
        if (fixture === 'linear-pendant' && tableLength) {
          lengthMin = tableLength * 0.5;
          lengthMax = tableLength * 0.67;
          sizeLabel = `${formatRange(lengthMin, lengthMax, unit)} long`;
        } else {
          sizeLabel = `${formatRange(min, max, unit)} wide`;
        }
        height = `${formatRange(30 + extraDrop, 36 + extraDrop, unit)} above the tabletop`;
        why = 'For dining tables, a fixture that is about one-half to two-thirds of the table width usually feels balanced without extending past the table edge.';
      } else if (group === 'island') {
        const islandLength = toInches(this.state.islandLength, unit);
        const islandWidth = toInches(this.state.islandWidth, unit) || 36;
        if (fixture === 'linear-pendant' || this.state.fixtureCountPreference === 'single') {
          lengthMin = islandLength * 0.5;
          lengthMax = islandLength * 0.67;
          sizeLabel = `${formatRange(lengthMin, lengthMax, unit)} long`;
          quantity = '1 linear fixture';
        } else {
          const usable = Math.max(0, islandLength - 20);
          let count = this.state.fixtureCountPreference === 'three' ? 3 : this.state.fixtureCountPreference === 'two' ? 2 : (usable >= 72 ? 3 : 2);
          const maxDiameter = Math.min(islandWidth * 0.45, (usable / count) - 10);
          if (maxDiameter < 10 && count > 2) count = 2;
          quantity = `${count} pendants`;
          min = Math.max(8, Math.min(14, maxDiameter * 0.75));
          max = Math.max(min + 2, maxDiameter);
          sizeLabel = `${formatRange(min, max, unit)} each`;
        }
        height = `${formatRange(30 + extraDrop, 36 + extraDrop, unit)} above the island surface`;
        why = 'Kitchen islands need edge clearance and even spacing. The recommendation leaves visual breathing room at each end while keeping the light centered over the work surface.';
      } else {
        const roomLengthFt = toInches(this.state.roomLength, unit) / 12;
        const roomWidthFt = toInches(this.state.roomWidth, unit) / 12;
        const target = roomLengthFt + roomWidthFt;
        min = target * 0.9;
        max = target * 1.1;
        if (this.state.roomType === 'bedroom') {
          min *= 0.82;
          max *= 0.92;
        } else if (this.state.roomType === 'entryway' || this.state.roomType === 'open-plan') {
          min *= 1.05;
          max *= 1.15;
        }
        sizeLabel = `${formatRange(min, max, unit)} diameter`;
        height = fixture === 'flush-mount' || fixture === 'semi-flush'
          ? 'Choose a drop that keeps comfortable clearance below the fixture.'
          : 'Maintain comfortable walking clearance below the fixture.';
        why = 'For open rooms, adding room length and width in feet gives a practical starting diameter in inches. The range is adjusted for room type and visual weight.';
      }

      if (fixture === 'flush-mount' || fixture === 'semi-flush') {
        height = 'Focus on fixture diameter and total drop; pendant hanging-height rules do not apply.';
      }
      if (fixture === 'wall-light') {
        height = 'Mounting height depends on wall location, sightline, and junction box placement.';
        quantity = '1 or paired as needed';
      }

      const result = {
        min,
        max,
        lengthMin,
        lengthMax,
        sizeLabel,
        height,
        quantity,
        why,
        warning
      };
      result.variant = this.matchVariant(result);
      return result;
    }

    matchVariant(result) {
      if (!this.product?.variants?.length) return null;
      const target = result.lengthMin ? ((result.lengthMin + result.lengthMax) / 2) : ((result.min + result.max) / 2);
      const metric = result.lengthMin ? 'length' : 'diameter';
      const candidates = this.product.variants
        .map((variant) => {
          const value = variant.dimensions[metric] || variant.dimensions.width || variant.dimensions.diameter || null;
          if (!value) return null;
          return { variant, value, distance: Math.abs(value - target), inside: value >= (result.lengthMin || result.min) && value <= (result.lengthMax || result.max) };
        })
        .filter(Boolean)
        .sort((a, b) => (a.inside === b.inside ? a.distance - b.distance : a.inside ? -1 : 1));
      if (!candidates.length) return null;
      const best = candidates[0];
      this.bestVariant = best.variant;
      if (!best.inside) {
        result.warning = 'None of the standard sizes falls directly within the suggested range. The closest available option is shown, or you can request a custom size.';
      }
      return best.variant;
    }

    renderResult() {
      if (!this.state.result) this.state.result = this.calculate();
      const result = this.state.result;
      const set = (key, value) => {
        const el = qs(`[data-size-result="${key}"]`, this.root);
        if (el) el.textContent = value || '-';
      };
      set('size', result.sizeLabel);
      set('height', result.height);
      set('quantity', result.quantity);
      set('variant', result.variant ? result.variant.title : (this.product ? 'Custom size recommended' : 'See matching lights'));
      set('why', result.why);
      const warning = qs('[data-size-result="warning"]', this.root);
      if (warning) {
        warning.textContent = result.warning || '';
        warning.hidden = !result.warning;
      }
      const productLink = qs('[data-size-result-link="product"]', this.root);
      if (productLink && this.product) {
        productLink.href = result.variant?.url || this.product.url;
        productLink.textContent = 'View This Product';
      }
      const customize = qs('[data-size-result-link="customize"]', this.root);
      if (customize) {
        const destination = new URL(customize.href, window.location.origin);
        destination.searchParams.set('source', 'size-recommender');
        if (this.product) destination.searchParams.set('product', this.product.handle);
        if (result.variant) destination.searchParams.set('variant', result.variant.id);
        destination.searchParams.set('recommended_size', result.sizeLabel);
        destination.searchParams.set('hanging_height', result.height);
        destination.searchParams.set('room', this.state.roomType);
        destination.searchParams.set('placement', this.state.placement);
        customize.href = destination.toString();
        customize.textContent = this.product ? 'Customize This Product' : 'Customize This Size';
      }
      const selectButton = qs('[data-size-action="select-variant"]', this.root);
      if (selectButton) selectButton.hidden = !result.variant;
    }

    selectRecommendedVariant() {
      const variant = this.bestVariant || this.state.result?.variant;
      if (!variant) return;
      const productPage = qs('[data-lumi-product-page]');
      if (!productPage) {
        window.location.href = variant.url || this.product?.url || '/collections/all';
        return;
      }
      variant.options.forEach((value, index) => {
        const group = qs(`[data-lumi-option-group][data-option-index="${index}"]`, productPage);
        const button = qsa('[data-lumi-option-value]', group).find((item) => item.dataset.lumiOptionValue === String(value));
        if (button) button.click();
      });
      const select = qs('[data-lumi-variant-select]', productPage);
      if (select) {
        select.value = String(variant.id);
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
      const dialog = this.root.closest('[data-solena-size-dialog]');
      dialog?.close();
    }

    saveRecommendation() {
      const result = this.state.result || this.calculate();
      const text = `Solena Lighting size recommendation: ${result.sizeLabel}. Hanging height: ${result.height}. Quantity: ${result.quantity}.`;
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(text).then(() => this.showAlert('Recommendation copied to your clipboard.'));
      } else {
        this.showAlert(text);
      }
    }

    pushEvent() {
      if (Array.isArray(window.dataLayer)) {
        window.dataLayer.push({
          event: 'size_recommender_complete',
          fixture_type: this.state.fixtureType,
          placement: this.state.placement,
          unit: this.state.unit
        });
      }
    }
  }

  const initRecommenders = (root = document) => {
    qsa('[data-solena-size-recommender]', root).forEach((item) => {
      if (item.dataset.solenaSizeReady === 'true') return;
      item.dataset.solenaSizeReady = 'true';
      new SizeRecommender(item);
    });
  };

  const initDialogs = (root = document) => {
    qsa('[data-solena-size-open]', root).forEach((button) => {
      if (button.dataset.solenaSizeTriggerReady === 'true') return;
      button.dataset.solenaSizeTriggerReady = 'true';
      button.addEventListener('click', () => {
        const id = button.getAttribute('aria-controls');
        const dialog = id ? document.getElementById(id) : null;
        if (!dialog) return;
        if (typeof dialog.showModal === 'function') dialog.showModal();
        else dialog.setAttribute('open', '');
        document.body.classList.add('lumi-modal-open');
        initRecommenders(dialog);
        qs('[data-solena-size-close]', dialog)?.focus();
      });
    });

    qsa('[data-solena-size-dialog]', root).forEach((dialog) => {
      if (dialog.dataset.solenaSizeDialogReady === 'true') return;
      dialog.dataset.solenaSizeDialogReady = 'true';
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) dialog.close();
      });
      dialog.addEventListener('close', () => document.body.classList.remove('lumi-modal-open'));
      qs('[data-solena-size-close]', dialog)?.addEventListener('click', () => dialog.close());
    });

    qsa('[data-solena-size-start]', root).forEach((button) => {
      if (button.dataset.solenaSizeStartReady === 'true') return;
      button.dataset.solenaSizeStartReady = 'true';
      button.addEventListener('click', () => {
        const tool = qs('[data-solena-size-recommender]');
        tool?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  };

  const init = (root = document) => {
    initRecommenders(root);
    initDialogs(root);
  };

  document.addEventListener('DOMContentLoaded', () => init());
  document.addEventListener('shopify:section:load', (event) => init(event.target));

  window.SolenaSizeRecommender = { init };
})();
